import { useEffect, useState } from "react";
import {
  Search,
  X,
  CheckCircle2,
  Loader2,
  Fingerprint,
  Mail,
  Phone,
  RotateCcw,
} from "lucide-react";
import { useSearchChapterMembersQuery, MemberSearchResult } from "../api/chapterApi";

function fullName(m: MemberSearchResult) {
  return [m.surname, m.firstname, m.othernames].filter(Boolean).join(" ");
}

interface MemberPickerProps {
  label: string;
  valueUserId: number | null;
  onPickUserId: (userId: number | null) => void;
  disabled?: boolean;
}

export default function MemberPicker({
  label,
  valueUserId,
  onPickUserId,
  disabled,
}: MemberPickerProps) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [selected, setSelected] = useState<MemberSearchResult | null>(null);

  // Clear local selection when parent resets to null
  useEffect(() => {
    if (valueUserId === null) setSelected(null);
  }, [valueUserId]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  const { data: rawData, isFetching } = useSearchChapterMembersQuery(debouncedQ, {
    skip: debouncedQ.trim().length < 2,
  });
  const data: MemberSearchResult[] = Array.isArray(rawData) ? rawData : [];

  const handlePick = (m: MemberSearchResult) => {
    setSelected(m);
    onPickUserId(Number(m.id));
    setOpen(false);
    setQ("");
    setDebouncedQ("");
  };

  const handleClear = () => {
    setSelected(null);
    onPickUserId(null);
  };

  const closeModal = () => {
    setOpen(false);
    setQ("");
    setDebouncedQ("");
  };

  return (
    <>
      <div className="space-y-1.5">
        <label className="text-xs font-bold text-gray-700 uppercase tracking-tight">
          {label}
        </label>

        {selected ? (
          <div className="p-4 bg-white border-2 border-gray-200 rounded-2xl space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center text-white font-bold text-base shrink-0">
                  {selected.surname?.[0] ?? "?"}
                </div>
                <div>
                  <div className="font-bold text-gray-900 leading-tight">
                    {fullName(selected)}
                  </div>
                  {selected.membership_no && (
                    <div className="text-[10px] text-gray-400 flex items-center gap-1 mt-0.5">
                      <Fingerprint className="w-3 h-3" /> {selected.membership_no}
                    </div>
                  )}
                </div>
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-all"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="space-y-1 border-t border-gray-100 pt-2">
              {selected.email && (
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <Mail className="w-3 h-3 shrink-0" /> {selected.email}
                </div>
              )}
              {selected.phone && (
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <Phone className="w-3 h-3 shrink-0" /> {selected.phone}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="py-8 flex flex-col items-center justify-center text-center space-y-3 border-2 border-dashed border-gray-300 rounded-2xl bg-gray-50">
            <p className="text-sm font-bold text-gray-500">No Member Selected</p>
            <button
              type="button"
              disabled={disabled}
              onClick={() => setOpen(true)}
              className="text-xs font-bold text-gray-900 underline underline-offset-4 hover:text-gray-600 disabled:opacity-40"
            >
              Click to Search
            </button>
          </div>
        )}
      </div>

      {/* Search modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/20 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white w-full max-w-lg rounded-[32px] shadow-2xl border border-gray-100 overflow-hidden animate-in slide-in-from-bottom-4">
            <div className="p-6 border-b border-gray-50">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900">Select {label}</h3>
                <button
                  type="button"
                  onClick={closeModal}
                  className="p-2 hover:bg-gray-100 rounded-full transition-all"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input
                  autoFocus
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-100 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-gray-900/5 transition-all"
                  placeholder="Search by name, email or Membership No..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
            </div>

            <div className="max-h-[350px] overflow-y-auto p-4 space-y-2">
              {isFetching ? (
                <div className="py-10 flex flex-col items-center gap-2 text-gray-400">
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span className="text-xs font-medium">
                    Searching member database...
                  </span>
                </div>
              ) : debouncedQ.trim().length < 2 ? (
                <div className="py-10 text-center text-xs font-bold text-gray-300 uppercase tracking-widest italic">
                  Start typing to search
                </div>
              ) : data.length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-400">
                  No matching members found.
                </div>
              ) : (
                data.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => handlePick(m)}
                    className="w-full text-left p-4 rounded-2xl border border-gray-100 bg-white hover:border-gray-900 hover:shadow-md transition-all flex items-center justify-between group"
                  >
                    <div>
                      <div className="font-bold text-gray-900">
                        {fullName(m)}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {m.membership_no && (
                          <span className="font-medium">
                            {m.membership_no} •{" "}
                          </span>
                        )}
                        {m.email}
                      </div>
                    </div>
                    <CheckCircle2 className="w-5 h-5 text-gray-200 group-hover:text-gray-900 transition-colors" />
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
